/**
 * Contas a receber: cada parcela que um cliente ainda vai pagar. Nasce de duas fontes:
 *  - as parcelas das vendas importadas do Bling (cliente pagou em 6x = 6 parcelas, cada uma com o
 *    seu vencimento), mantidas em sincronia pelo webhook;
 *  - vendas parceladas registradas aqui (televendas, venda combinada por telefone, lançamento
 *    manual): "6x de R$ 500" gera as 6 parcelas, e a soma é sempre o valor da venda.
 *
 * Parcela vencida de venda já ATENDIDA no Bling entra como recebida PRESUMIDA (selo "presumida"):
 * o Bling não informa a baixa de cada parcela. O operador pode reabrir qualquer uma.
 *
 * Fonte: `/api/platform/finance/receivables` (migration 0056).
 */
import { createElement as h, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CircleAlert, Coins, Plus, Wallet } from "lucide-react";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { DueBadge, InstallmentPreview } from "../components/FinanceBits.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatCount } from "../format.js";
import { salesChannelLabel } from "../orders/channels.js";
import { addDays, brlToCents, centsToInput, formatDate, todayIso } from "../finance/format.js";
import type { NewReceivableInput, Receivable, SettleInput } from "../finance/types.js";
import { useFinanceMutations, useReceivables } from "../queries.js";
import { Badge, Button, Card, Drawer, EmptyState, Field, PageHeader, Segmented, StatTile, controlClass } from "../ui/index.js";

export type ReceivableFilter = "open" | "overdue" | "received" | "all";
const PAGE_SIZE = 100;

export function receivableSummary(receivables: Receivable[], today: string) {
  const open = receivables.filter((r) => r.status === "open");
  const overdue = open.filter((r) => r.dueDate < today);
  const next30 = open.filter((r) => r.dueDate >= today && r.dueDate <= addDays(today, 30));
  const monthPrefix = today.slice(0, 7);
  const receivedMonth = receivables.filter((r) => r.status === "received" && (r.receivedAt ?? "").slice(0, 7) === monthPrefix);
  const sum = (rows: Receivable[], pick: (r: Receivable) => number) => rows.reduce((acc, r) => acc + pick(r), 0);
  return {
    openCents: sum(open, (r) => r.amountCents),
    openCount: open.length,
    overdueCents: sum(overdue, (r) => r.amountCents),
    overdueCount: overdue.length,
    next30Cents: sum(next30, (r) => r.amountCents),
    next30Count: next30.length,
    receivedMonthCents: sum(receivedMonth, (r) => r.receivedAmountCents ?? r.amountCents)
  };
}

export function filterReceivables(receivables: Receivable[], filter: ReceivableFilter, channel: string, query: string, today: string): Receivable[] {
  const needle = query.trim().toLowerCase();
  return receivables
    .filter((r) => {
      if (filter === "open") return r.status === "open";
      if (filter === "overdue") return r.status === "open" && r.dueDate < today;
      if (filter === "received") return r.status === "received";
      return true;
    })
    .filter((r) => channel === "todos" || (r.channelKind ?? r.source) === channel)
    .filter((r) => !needle || [r.customerName ?? "", r.orderRef ?? "", r.description ?? ""].some((value) => value.toLowerCase().includes(needle)))
    .sort((a, b) => (filter === "received" ? (b.receivedAt ?? "").localeCompare(a.receivedAt ?? "") : a.dueDate.localeCompare(b.dueDate)));
}

interface SaleFormState {
  customerName: string;
  description: string;
  amount: string;
  firstDueDate: string;
  installments: string;
  paymentMethod: string;
  notes: string;
}

function NewSaleForm({ today, onSubmit, busy, error }: { today: string; onSubmit: (input: NewReceivableInput) => void; busy: boolean; error: string | null }): ReactElement {
  const [form, setForm] = useState<SaleFormState>({ customerName: "", description: "", amount: "", firstDueDate: today, installments: "1", paymentMethod: "", notes: "" });
  const amountCents = brlToCents(form.amount);
  const installments = Math.max(1, Math.min(120, Math.trunc(Number(form.installments) || 1)));
  const valid = form.customerName.trim() !== "" && amountCents !== null && /^\d{4}-\d{2}-\d{2}$/.test(form.firstDueDate);
  const set = (key: keyof SaleFormState) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value });
  return h(
    "form",
    {
      className: "flex flex-col gap-4",
      "data-form": "nova-venda-parcelada",
      onSubmit: (event: { preventDefault: () => void }) => {
        event.preventDefault();
        if (!valid || amountCents === null) return;
        onSubmit({
          customerName: form.customerName.trim(),
          description: form.description.trim() || undefined,
          amountCents,
          firstDueDate: form.firstDueDate,
          installments,
          paymentMethod: form.paymentMethod.trim() || undefined,
          notes: form.notes.trim() || undefined,
          source: "televendas"
        });
      }
    },
    h(Field, { label: "Cliente", children: ({ id }) => h("input", { id, className: controlClass, value: form.customerName, onChange: set("customerName"), required: true }) }),
    h(Field, { label: "O que foi vendido?", children: ({ id }) => h("input", { id, className: controlClass, value: form.description, onChange: set("description"), placeholder: "Ex.: Laser 20W + rotativo" }) }),
    h("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-3" },
      h(Field, { label: "Valor total (R$)", children: ({ id }) => h("input", { id, className: controlClass, inputMode: "decimal", value: form.amount, onChange: set("amount"), placeholder: "0,00", required: true }) }),
      h(Field, { label: "1º vencimento", children: ({ id }) => h("input", { id, className: controlClass, type: "date", value: form.firstDueDate, onChange: set("firstDueDate"), required: true }) }),
      h(Field, { label: "Parcelas", hint: "1 = à vista", children: ({ id }) => h("input", { id, className: controlClass, type: "number", min: 1, max: 120, value: form.installments, onChange: set("installments") }) })
    ),
    h(InstallmentPreview, { amountCents, installments, firstDueDate: form.firstDueDate }),
    h(Field, { label: "Forma de pagamento", children: ({ id }) => h("input", { id, className: controlClass, value: form.paymentMethod, onChange: set("paymentMethod"), placeholder: "Ex.: boleto, cartão, PIX" }) }),
    h(Field, { label: "Observações", children: ({ id }) => h("textarea", { id, className: controlClass, rows: 2, value: form.notes, onChange: set("notes") }) }),
    error ? h("p", { className: "m-0 text-sm text-danger", role: "alert" }, error) : null,
    h(Button, { type: "submit", variant: "primary", disabled: !valid || busy }, busy ? "Salvando…" : "Registrar venda parcelada")
  );
}

function ReceiveRow({ receivable, today, busy, onSettle, onAction }: { receivable: Receivable; today: string; busy: boolean; onSettle: (id: string, input: SettleInput) => void; onAction: (id: string, action: "cancel" | "reopen") => void }): ReactElement {
  const [receiving, setReceiving] = useState(false);
  const [receivedAt, setReceivedAt] = useState(today);
  const [amount, setAmount] = useState(centsToInput(receivable.amountCents));
  const amountCents = brlToCents(amount);
  const channelKey = receivable.channelKind ?? receivable.source;
  return h(
    "tr",
    { className: "border-t border-border align-top", "data-receivable": receivable.id, "data-status": receivable.status },
    h("td", { className: "px-2 py-3 tabular-nums" }, formatDate(receivable.dueDate)),
    h("td", { className: "px-2 py-3" },
      h("span", { className: "font-semibold text-ink" }, receivable.customerName ?? "Cliente não informado"),
      receivable.installmentsTotal > 1 ? h("span", { className: "ml-2 text-xs text-muted" }, `parcela ${receivable.installmentNo}/${receivable.installmentsTotal}`) : null,
      h("p", { className: "m-0 text-xs text-muted" },
        receivable.platformOrderId
          ? h(Link, { to: `/pedidos/${receivable.platformOrderId}`, className: "font-semibold text-brand-text" }, `Pedido ${receivable.orderRef ?? ""}`.trim())
          : receivable.description ?? "Lançamento manual"
      )
    ),
    h("td", { className: "px-2 py-3 text-sm text-muted" }, salesChannelLabel(channelKey), receivable.paymentMethod ? ` · ${receivable.paymentMethod}` : ""),
    h("td", { className: "px-2 py-3 text-right font-extrabold tabular-nums" }, formatBrl(receivable.status === "received" ? (receivable.receivedAmountCents ?? receivable.amountCents) : receivable.amountCents)),
    h("td", { className: "px-2 py-3" },
      h("span", { className: "flex flex-col items-start gap-1" },
        h(DueBadge, { status: receivable.status === "received" ? "settled" : receivable.status, dueDate: receivable.dueDate, today, settledLabel: "Recebida", settledAt: receivable.receivedAt }),
        receivable.presumed ? h(Badge, { tone: "neutral", title: "Vencida, de venda já atendida no Bling. O Bling não informa a baixa de cada parcela.", "data-presumed": "true" }, "presumida") : null
      )
    ),
    h("td", { className: "px-2 py-3" },
      receivable.status === "open" && !receiving
        ? h("span", { className: "flex flex-wrap gap-2" },
            h(Button, { size: "sm", variant: "primary", onClick: () => setReceiving(true), disabled: busy, "data-action": "receber" }, "Receber"),
            h(Button, { size: "sm", variant: "ghost", onClick: () => onAction(receivable.id, "cancel"), disabled: busy }, "Cancelar")
          )
        : receivable.status === "open"
          ? h("span", { className: "flex flex-wrap items-end gap-2" },
              h("input", { "aria-label": "Data do recebimento", type: "date", className: controlClass + " w-40", value: receivedAt, onChange: (e: { target: { value: string } }) => setReceivedAt(e.target.value) }),
              h("input", { "aria-label": "Valor recebido (R$)", inputMode: "decimal", className: controlClass + " w-28", value: amount, onChange: (e: { target: { value: string } }) => setAmount(e.target.value) }),
              h(Button, { size: "sm", variant: "primary", disabled: busy || amountCents === null || !receivedAt, onClick: () => { if (amountCents !== null) { onSettle(receivable.id, { action: "receive", at: receivedAt, amountCents }); setReceiving(false); } }, "data-action": "confirmar-recebimento" }, "Confirmar"),
              h(Button, { size: "sm", variant: "ghost", onClick: () => setReceiving(false) }, "Voltar")
            )
          : h(Button, { size: "sm", variant: "ghost", onClick: () => onAction(receivable.id, "reopen"), disabled: busy }, "Reabrir")
    )
  );
}

export interface ContasReceberViewProps {
  /** `null` = rota não publicada neste ambiente. */
  receivables: Receivable[] | null;
  today?: string;
  onCreate?: (input: NewReceivableInput) => void;
  onSettle?: (id: string, input: SettleInput) => void;
  onAction?: (id: string, action: "cancel" | "reopen") => void;
  busy?: boolean;
  error?: string | null;
  initialFilter?: ReceivableFilter;
}

export function ContasReceberView({ receivables, today = todayIso(), onCreate, onSettle, onAction, busy = false, error = null, initialFilter = "open" }: ContasReceberViewProps): ReactElement {
  const [filter, setFilter] = useState<ReceivableFilter>(initialFilter);
  const [channel, setChannel] = useState("todos");
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE_SIZE);
  const [creating, setCreating] = useState(false);
  const header = h(PageHeader, {
    title: "Contas a receber",
    description: "Cada parcela que um cliente ainda vai pagar — das vendas do Bling e das vendas parceladas registradas aqui.",
    actions: onCreate ? h(Button, { variant: "primary", onClick: () => setCreating(true), "data-action": "nova-venda" }, h(Plus, { className: "h-4 w-4", "aria-hidden": true }), "Venda parcelada") : undefined
  });

  if (receivables === null) {
    return h("div", { className: "page page--contas-receber flex flex-col gap-6", "data-page": "contas-receber" }, header,
      h(StatusPanel, { kind: "awaiting_source", title: `Contas a receber ${AWAITING_CONTRACT_LABEL}`, detail: "A rota /api/platform/finance/receivables não respondeu neste ambiente." }));
  }

  const summary = receivableSummary(receivables, today);
  const visible = filterReceivables(receivables, filter, channel, query, today);
  const channels = [...new Set(receivables.map((r) => r.channelKind ?? r.source))].sort();
  return h(
    "div",
    { className: "page page--contas-receber flex flex-col gap-6", "data-page": "contas-receber" },
    header,
    h("section", { "aria-label": "Resumo de contas a receber", className: "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" },
      h(StatTile, { label: "A receber", value: formatBrl(summary.openCents), hint: `${formatCount(summary.openCount)} parcela(s) em aberto`, icon: Wallet, "data-kpi": "receber-aberto" }),
      h(StatTile, { label: "Vencidas", value: formatBrl(summary.overdueCents), hint: `${formatCount(summary.overdueCount)} parcela(s) atrasada(s)`, icon: CircleAlert, "data-kpi": "receber-vencidas" }),
      h(StatTile, { label: "Próximos 30 dias", value: formatBrl(summary.next30Cents), hint: `${formatCount(summary.next30Count)} parcela(s)`, icon: CalendarClock, "data-kpi": "receber-30d" }),
      h(StatTile, { label: "Recebido no mês", value: formatBrl(summary.receivedMonthCents), icon: Coins, "data-kpi": "receber-mes" })
    ),
    error ? h(StatusPanel, { kind: "error", title: "Não foi possível concluir a operação", detail: error }) : null,
    h(Card, null,
      h("div", { className: "mb-4 flex flex-wrap items-end justify-between gap-3" },
        h(Segmented<ReceivableFilter>, {
          label: "Filtrar parcelas",
          value: filter,
          options: [{ value: "open", label: "Em aberto" }, { value: "overdue", label: "Vencidas" }, { value: "received", label: "Recebidas" }, { value: "all", label: "Todas" }],
          onChange: (value) => { setFilter(value); setShown(PAGE_SIZE); }
        }),
        h("div", { className: "flex flex-wrap gap-3" },
          h("select", { "aria-label": "Canal", className: controlClass, value: channel, "data-filter-control": "channel", onChange: (event: { target: { value: string } }) => { setChannel(event.target.value); setShown(PAGE_SIZE); } },
            h("option", { value: "todos" }, "Todos os canais"),
            channels.map((key) => h("option", { key, value: key }, salesChannelLabel(key)))
          ),
          h("input", { type: "search", "aria-label": "Buscar parcela", placeholder: "Cliente, pedido ou descrição", className: controlClass + " w-full sm:w-64", value: query, onChange: (event: { target: { value: string } }) => { setQuery(event.target.value); setShown(PAGE_SIZE); } })
        )
      ),
      visible.length === 0
        ? h(EmptyState, { title: receivables.length === 0 ? "Nenhuma parcela a receber" : "Nenhuma parcela neste filtro", description: receivables.length === 0 ? "As parcelas das vendas do Bling aparecem aqui; use “Venda parcelada” para registrar uma venda combinada por fora." : "Ajuste o filtro ou a busca." })
        : h("div", { className: "overflow-x-auto" },
            h("table", { className: "w-full text-left text-sm", "data-table": "contas-receber" },
              h("thead", null, h("tr", { className: "text-xs uppercase text-muted" },
                ["Vencimento", "Cliente / pedido", "Canal", "Valor", "Situação", "Ações"].map((title, index) => h("th", { key: title, scope: "col", className: "px-2 py-2" + (index === 3 ? " text-right" : "") }, title)))),
              h("tbody", null, visible.slice(0, shown).map((receivable) => h(ReceiveRow, { key: receivable.id, receivable, today, busy, onSettle: (id, input) => onSettle?.(id, input), onAction: (id, action) => onAction?.(id, action) })))
            ),
            visible.length > shown ? h("div", { className: "mt-3 flex items-center gap-3 text-sm text-muted" }, h("span", null, `Mostrando ${shown} de ${formatCount(visible.length)}`), h(Button, { size: "sm", onClick: () => setShown(shown + PAGE_SIZE) }, "Mostrar mais")) : null
          )
    ),
    onCreate
      ? h(Drawer, { open: creating, title: "Registrar venda parcelada", onClose: () => setCreating(false), children: h(NewSaleForm, { today, busy, error, onSubmit: (input) => { onCreate(input); setCreating(false); } }) })
      : null
  );
}

export function ContasReceber(): ReactElement {
  const query = useReceivables();
  const { createReceivable, updateReceivable } = useFinanceMutations();
  if (query.isPending) return h(StatusPanel, { kind: "loading", title: "Carregando contas a receber…" });
  if (query.error) return h(StatusPanel, { kind: "error", title: "Não foi possível carregar as contas a receber", detail: errorDetail(query.error) });
  const mutationError = createReceivable.error ?? updateReceivable.error;
  return h(ContasReceberView, {
    receivables: query.data ?? null,
    onCreate: (input) => createReceivable.mutate(input),
    onSettle: (id, input) => updateReceivable.mutate({ id, input }),
    onAction: (id, action) => updateReceivable.mutate({ id, input: { action } }),
    busy: createReceivable.isPending || updateReceivable.isPending,
    error: mutationError ? mutationError.message : null
  });
}
